import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import isUndefined from 'lodash/isUndefined';

import {
  FUNNELS_MATH_TYPES,
  NUMERIC_MATH_TYPES,
  PER_USER_AGGREGATIONS,
  PRIMITIVE_MATH_TYPES,
  RETENTION_MATH_TYPES,
  XAU_MATH_TYPES,
} from 'common/report/constants';
import {MetricType} from 'common/query-builder/query-block/constants';

import {
  BehaviorMetric,
  BehaviorShowClause as BehaviorShowClauseType,
  FormulaMeasurement,
  FormulaShowClause as FormulaShowClauseType,
  InsightsResourceType,
  OldShowClause,
  ShowClause as ShowClauseType,
  ShowClauseFormula,
} from '../util/types';
import {DATASET_MIXPANEL} from './constants';
import {isFormulaShowClause, isValidFormula, sanitizeIfCustomProperty, transformShowToBehaviorMetric} from './utils';
import {FilterClause} from './filter-clause';
import {ClauseType} from './types';

export abstract class ShowClause {
  static TYPE: typeof ClauseType.Show = ClauseType.Show;
  public isHidden;
  public isExpanded;

  static createShowClause(attrs: ShowClauseType): ShowClause {
    if (isFormulaShowClause(attrs)) {
      return new FormulaShowClause(attrs);
    } else {
      return new BehaviorShowClause(attrs);
    }
  }

  abstract get attrs(): ShowClauseType;

  abstract get valid(): boolean;

  serialize(): ShowClauseType {
    return this.attrs;
  }

  abstract isRollingClause(): boolean;

  abstract isCumulativeClause(): boolean;

  abstract extend(attrs: Partial<ShowClauseType>): ShowClause;

  abstract toBookmarkData(): ShowClauseType;

  abstract toUrlData(): ShowClauseType;
}

/**
 * This union type fixes the following problem with using class ShowClause as the type:
 *
 *   const showClause: ShowClause = ...;
 *   if (showClause instanceof BehaviorShowClause) {
 *     ...
 *   } else {
 *     // showClause's type here is `ShowClause`
 *     if (showClause instanceof FormulaShowClause) {
 *       // so `instanceof FormulaShowClause` needed to narrow type
 *     }
 *   }
 *
 * But if showClause is typed as ShowClauseClassType:
 *
 *   const showClause: ShowClauseClassType = ...;
 *   if (showClause instanceof BehaviorShowClause) {
 *     ...
 *   } else {
 *     // showClause's type here is `FormulaShowClause`
 *   }
 */
export type ShowClauseClassType = BehaviorShowClause | FormulaShowClause;

export class BehaviorShowClause extends ShowClause {
  behavior: BehaviorMetric['behavior'];
  measurement: BehaviorMetric['measurement'];

  // eslint-disable-next-line sonarjs/cognitive-complexity
  constructor(attrs: BehaviorShowClauseType) {
    super();
    const {behavior, measurement} = transformShowToBehaviorMetric(attrs);

    this.behavior = behavior;
    this.measurement = measurement;
    this.behavior.dataset = behavior.dataset || null;
    this.behavior.dataset = behavior.dataset === `mixpanel` ? DATASET_MIXPANEL : behavior.dataset;
    this.behavior.resourceType = behavior.resourceType || InsightsResourceType.Events;
    this.behavior.profileType = behavior.profileType || null;
    this.behavior.search = behavior.search || ``;
    this.behavior.dataGroupId = behavior.dataGroupId || null;

    if (attrs.isHidden === true) {
      this.isHidden = true;
    }

    if (attrs.isExpanded === true) {
      this.isExpanded = true;
    }

    if (behavior.type) {
      this.behavior.type = behavior.type;
    }
    if (behavior.customBucket) {
      this.behavior.customBucket = behavior.customBucket;
    }

    if (this.behavior.resourceType === InsightsResourceType.People) {
      this.behavior.profileType = InsightsResourceType.People;
    }

    if (behavior.filters && behavior.filters.length) {
      this.behavior.filtersDeterminer = behavior.filtersDeterminer;
      this.behavior.filters = behavior.filters;
    }

    if (behavior.behaviors && behavior.behaviors.length) {
      this.behavior.behaviors = behavior.behaviors;
    }

    if (behavior.conversionWindowDuration) {
      this.behavior.conversionWindowDuration = behavior.conversionWindowDuration;
    }
    if (behavior.conversionWindowUnit) {
      this.behavior.conversionWindowUnit = behavior.conversionWindowUnit;
    }
    if (behavior.funnelOrder) {
      this.behavior.funnelOrder = behavior.funnelOrder;
    }
    if (behavior.exclusions) {
      this.behavior.exclusions = behavior.exclusions;
    }
    if (behavior.aggregateBy) {
      this.behavior.aggregateBy = behavior.aggregateBy;
    }

    // Retention specific fields
    if (behavior.retentionType) {
      this.behavior.retentionType = behavior.retentionType;
    }
    if (behavior.retentionAlignmentType) {
      this.behavior.retentionAlignmentType = behavior.retentionAlignmentType;
    }
    if (behavior.retentionUnit) {
      this.behavior.retentionUnit = behavior.retentionUnit;
    }
    if (behavior.retentionUnbounded) {
      this.behavior.retentionUnbounded = behavior.retentionUnbounded;
    }
    if (behavior.retentionCustomBucketSizes) {
      this.behavior.retentionCustomBucketSizes = behavior.retentionCustomBucketSizes;
    }

    const defaultMathType = [MetricType.Retention, MetricType.RetentionFrequency].includes(this.behavior.type)
      ? RETENTION_MATH_TYPES.RETENTION_RATE
      : PRIMITIVE_MATH_TYPES.TOTAL;
    this.measurement.math = measurement.math || defaultMathType;
    this.measurement.perUserAggregation = measurement.perUserAggregation || null;
    this.measurement.rolling = measurement.rolling || null;
    this.measurement.cumulative = measurement.cumulative || false;

    if (measurement.retentionCumulative !== undefined) {
      this.measurement.retentionCumulative = measurement.retentionCumulative;
    }

    // TODO: Move sanitization logic app api bookmark endpoint
    this.measurement.property = measurement.property
      ? omit(sanitizeIfCustomProperty(measurement.property), `label`)
      : null;

    if (measurement.multiAttribution) {
      this.measurement.multiAttribution = measurement.multiAttribution;
    }

    // Funnels specific fields
    if (measurement.stepIndex) {
      this.measurement.stepIndex = measurement.stepIndex;
    }

    // Retention specific fields
    if (measurement.retentionBucketIndex) {
      this.measurement.retentionBucketIndex = measurement.retentionBucketIndex;
    }
  }

  serializeFilter() {
    if (this.behavior.filters?.length) {
      const filters = this.behavior.filters.map((clause, index) => ({
        ...new FilterClause(clause).serialize(),
        // Assign determiner to the first filter
        ...(index === 0 ? {determiner: this.behavior.filtersDeterminer} : {}),
      }));

      return {filters};
    } else {
      return {};
    }
  }

  getInlineCohort() {
    return this.behavior.raw_cohort || null;
  }

  get isInlineCohort() {
    return !!this.getInlineCohort();
  }

  get attrs(): BehaviorShowClauseType {
    return {
      ...(this.isHidden ? {isHidden: true} : {}),
      ...(this.isExpanded ? {isExpanded: true} : {}),
      // TODO: fix typing so that as BehaviorMetric[...] is not needed
      behavior: omitBy(
        {
          ...this.behavior,
          ...this.serializeFilter(),
        },
        isUndefined,
      ) as BehaviorMetric['behavior'],
      measurement: omitBy(
        {
          ...this.measurement,
        },
        isUndefined,
      ) as BehaviorMetric['measurement'],
    };
  }

  get customEventId() {
    if (this.behavior.type === MetricType.CustomEvent) {
      return this.behavior.id;
    }
    return null;
  }

  get valid(): boolean {
    let isValidValue = true;
    if (this.behavior.type === MetricType.Funnel) {
      isValidValue = true;
    }
    if (this.behavior.type === MetricType.CustomEvent) {
      isValidValue = !!this.behavior.id;
    }
    if (this.behavior.type === MetricType.Cohort) {
      isValidValue = !!this.behavior.id || !!this.behavior.raw_cohort;
    }
    if (this.behavior.type === MetricType.People) {
      isValidValue = !!this.behavior.name;
    }
    const isValidMathType = [
      ...Object.values(PRIMITIVE_MATH_TYPES),
      ...Object.values(NUMERIC_MATH_TYPES),
      ...Object.values(XAU_MATH_TYPES),
      ...Object.values(PER_USER_AGGREGATIONS),
      ...Object.values(FUNNELS_MATH_TYPES),
      ...Object.values(RETENTION_MATH_TYPES),
    ].includes(this.measurement.math);
    return isValidValue && isValidMathType;
  }

  extend(attrs: Partial<BehaviorShowClauseType> = {}): BehaviorShowClause {
    const {behavior, measurement} = transformShowToBehaviorMetric(attrs);
    return new BehaviorShowClause({
      behavior: {
        ...this.behavior,
        ...omitBy(behavior, isUndefined),
        type: this.behavior.type,
      },
      measurement: {
        ...this.measurement,
        ...omitBy(measurement, isUndefined),
      },
    });
  }

  toBookmarkData(): ShowClauseType {
    const attrs = this.attrs;
    return {
      ...(this.isHidden ? {isHidden: true} : {}),
      ...(this.isExpanded ? {isExpanded: true} : {}),
      behavior: {
        ...attrs.behavior,
      },
      measurement: attrs.measurement,
    };
  }

  isXauClause(): boolean {
    return Object.values(XAU_MATH_TYPES).includes(this.measurement.math as XAU_MATH_TYPES);
  }

  isRollingClause(): boolean {
    return !!this.measurement.rolling;
  }

  isCumulativeClause(): boolean {
    return !!this.measurement.cumulative;
  }

  _pickValueEntries(): OldShowClause['value'] {
    const {type, id, name, raw_cohort: rawCohort, renamed} = this.behavior;
    if (type === MetricType.Cohort) {
      return {
        ...(id ? {id} : {}),
        name,
        ...(renamed ? {renamed} : {}),
        resourceType: InsightsResourceType.Cohorts,
        ...(rawCohort ? {raw_cohort: rawCohort} : {}),
      };
    }
    if (type === MetricType.CustomEvent) {
      return {
        id,
        renamed,
        resourceType: InsightsResourceType.Events,
        custom: true,
      };
    }
    return {name, ...(renamed ? {renamed} : {}), ...(name ? {resourceType: InsightsResourceType.Events} : {})};
  }

  toUrlData(): BehaviorShowClauseType {
    return this.attrs;
  }
}

export class FormulaShowClause extends ShowClause {
  measurement: FormulaMeasurement;
  formula: ShowClauseFormula;

  constructor(attrs: FormulaShowClauseType) {
    super();
    this.formula = attrs.formula;
    this.measurement = attrs.measurement || {};

    if (attrs.isHidden === true) {
      this.isHidden = true;
    }

    if (attrs.isExpanded === true) {
      this.isExpanded = true;
    }
  }

  get attrs(): FormulaShowClauseType {
    return this.serializeFormula();
  }

  private serializeFormula(): FormulaShowClauseType {
    return {
      ...(this.isHidden ? {isHidden: true} : {}),
      ...(this.isExpanded ? {isExpanded: true} : {}),
      formula: {...this.formula},
      measurement: {...this.measurement},
    };
  }

  get valid(): boolean {
    return isValidFormula(this.formula);
  }

  isRollingClause(): boolean {
    return !!this.measurement.rolling;
  }

  isCumulativeClause(): boolean {
    return !!this.measurement.cumulative;
  }

  extend(attrs: Partial<FormulaShowClauseType> = {}): FormulaShowClause {
    return new FormulaShowClause({
      formula: {
        ...this.formula,
        ...attrs.formula,
      },
      measurement: {
        ...this.measurement,
        ...attrs.measurement,
      },
    });
  }

  toBookmarkData(): FormulaShowClauseType {
    return this.serializeFormula();
  }

  toUrlData(): FormulaShowClauseType {
    return this.serializeFormula();
  }
}
